import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from 'emailjs-com';

const Contact = () => {
  const { t } = useTranslation();
  const formRef = useRef();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('contact.errorName', 'Ismingizni kiriting');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errorEmail', 'Email manzilingizni kiriting');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errorEmailValid', 'Amal qiluvchi email kiriting');
    }
    if (!formData.subject.trim()) newErrors.subject = t('contact.errorSubject', 'Mavzu kiriting');
    if (!formData.message.trim()) newErrors.message = t('contact.errorMessage', 'Xabaringizni yozing');
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSending) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSending(true);
    setStatusMessage('');

    const SERVICE_ID = 'service_owckg0d';
    const TEMPLATE_ID = 'template_24iyt0f';
    const USER_ID = '-2N6L1t_7_exfkOhj';

    emailjs
      .send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message
        },
        USER_ID
      )
      .then(
        (result) => {
          console.log('Email successfully sent:', result.text);
          setStatusMessage(t('contact.successMessage', 'Xabaringiz muvaffaqiyatli yuborildi! Tez orada bog‘lanamiz.'));
          setSubmitted(true);
          formRef.current.reset();
          setFormData({ name: '', email: '', subject: '', message: '' });
        },
        (error) => {
          console.error('Email send error:', error.text);
          setStatusMessage(t('contact.errorSend', 'Xabar yuborishda xatolik yuz berdi.'));
        }
      )
      .finally(() => {
        setIsSending(false);
        setTimeout(() => setSubmitted(false), 3000);
      });
  };

  return (
    <div className="flex flex-col space-y-16">
      {/* --- Banner --- */}
      <section
        className="relative h-64 bg-cover bg-center"
        style={{ backgroundImage: "url('https://plus.unsplash.com/premium_photo-1658506646178-e4ef5810361b?q=80&w=2072&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')" }}
      >
        <div className="absolute inset-0 bg-black opacity-50" />
        <div className="relative container mx-auto h-full flex items-center justify-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg">
            {t('contact.title', 'Biz bilan bog‘laning')}
          </h1>
        </div>
      </section>

      {/* --- Kontakt ma'lumotlari va ishlash vaqtlari --- */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-etmgreen">
              {t('contact.infoTitle', 'Kontakt ma’lumotlari')}
            </h2>
            <div className="space-y-4 text-gray-700">
              <div className="flex items-start">
                <span className="text-2xl mr-4">📍</span>
                <div>
                  <h3 className="font-semibold">{t('contact.addressTitle', 'Manzil')}</h3>
                  <p>{t('contact.addressText', 'Navoiy sh., Me‘morlar ko‘chasi, 16 G uy')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-4">📞</span>
                <div>
                  <h3 className="font-semibold">{t('contact.phoneTitle', 'Telefon')}</h3>
                  <p>+998 93 315 99 39</p>
                  <p>+998 90 611 72 43</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-4">✉️</span>
                <div>
                  <h3 className="font-semibold">{t('contact.emailTitle', 'Email')}</h3>
                  <p>info@navoietm.uz</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-4">⏰</span>
                <div>
                  <h3 className="font-semibold">{t('contact.hoursTitle', 'Ish vaqti')}</h3>
                  <p>{t('contact.hoursText', 'Dushanba – Juma: 09:00 – 18:00')}</p>
                  <p>{t('contact.hoursWeekend', 'Shanba – Yakshanba: Dam olish')}</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-semibold text-etmgreen mb-4">
                {t('contact.mapTitle', 'Xaritada ko‘ring')}
              </h3>
              <div className="w-full aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  title="Navoi ETM Xaritasi"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d322.71319588981936!2d65.37607335534943!3d40.11605633469061!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3f51c72a13233c7f%3A0x951ec8b3389d6d69!2zNDk4RytDRjQsINCd0LDQstC-0LgsINCh0LDQvNCw0YDQutCw0L3QtNGB0LrQsNGPINC-0LHQu9Cw0YHRgtGMLCDQo9C30LHQtdC60LjRgdGC0LDQvQ!5e1!3m2!1sru!2s!4v1771137032089!5m2!1sru!2s"
                  className="w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold text-etmgreen mb-6">
              {t('contact.formTitle', 'Xabar qoldiring')}
            </h2>

            {statusMessage && (
              <div
                className={`mb-4 p-4 rounded-lg ${
                  submitted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {statusMessage}
              </div>
            )}

            {!submitted && (
              <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
                {/* Ism */}
                <div>
                  <label htmlFor="name" className="block text-gray-700 mb-1">
                    {t('contact.nameLabel', 'Ism')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-etmgreen ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-gray-700 mb-1">
                    {t('contact.emailLabel', 'Email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-etmgreen ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Mavzu */}
                <div>
                  <label htmlFor="subject" className="block text-gray-700 mb-1">
                    {t('contact.subjectLabel', 'Mavzu')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-etmgreen ${
                      errors.subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                  )}
                </div>

                {/* Xabar */}
                <div>
                  <label htmlFor="message" className="block text-gray-700 mb-1">
                    {t('contact.messageLabel', 'Xabar')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-etmgreen ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                  ></textarea>
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSending}
                  className={`w-full text-white py-3 rounded-lg font-semibold transition ${
                    isSending ? 'bg-gray-400 cursor-not-allowed' : 'bg-etmgreen hover:bg-green-600'
                  }`}
                >
                  {isSending
                    ? t('contact.sending', 'Yuborilyapti...')
                    : t('contact.sendButton', 'Yuborish')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
